import axios, { AxiosRequestConfig } from 'axios';
import errorResponseHandler from '@/Services/Clients/errorResponseHandler';
import tokenManager from '@/Utils/tokenManager';
import { useContextDataStore } from '@/VueCore/stores/contextDataStore';
import { ErrorCode } from '@/Types/Enums/errorCode';

export default class ApiClient {
  /**
   * Returns a promise for a GET request
   * @param {string} url
   * @param {ErrorCode[]} [wellKnownErrorCodes] Well-known error codes that if returned should not be logged
   * @return {Promise}
   */
  public static async get(url: string, wellKnownErrorCodes: ErrorCode[] = []): Promise<any> {
    try {
      const response = await axios.get(url, this.createAxiosConfig());
      return response.data;
    } catch (error: any) {
      const applicationError = errorResponseHandler.handleErrorResponse(error, wellKnownErrorCodes);
      throw applicationError;
    }
  }

  /**
   * Returns a promise for a PUT request. Put should be used to update a resource that already exists
   * @param {string} url
   * @param {*} data JavaScript object
   * @param {ErrorCode[]} [wellKnownErrorCodes] Well-known error codes that if returned should not be logged
   * @return {Promise}
   */
  public static async put(url: string, data: any, wellKnownErrorCodes: ErrorCode[] = []): Promise<any> {
    try {
      const response = await axios.put(url, data, this.createAxiosConfig(data));
      return response.data;
    } catch (error: any) {
      const applicationError = errorResponseHandler.handleErrorResponse(error, wellKnownErrorCodes);
      throw applicationError;
    }
  }

  /**
   * Returns a promise for a POST request. Post should be used to create a new resource
   * @param {string} url
   * @param {*} data JavaScript object
   * @param {ErrorCode[]} [wellKnownErrorCodes] Well-known error codes that if returned should not be logged
   * @return {Promise}
   */
  public static async post(url: string, data: any, wellKnownErrorCodes: ErrorCode[] = []): Promise<any> {
    try {
      const response = await axios.post(url, data, this.createAxiosConfig(data));
      return response.data;
    } catch (error: any) {
      const applicationError = errorResponseHandler.handleErrorResponse(error, wellKnownErrorCodes);
      throw applicationError;
    }
  }

  /**
   * Returns a promise for a DELETE request
   * @param {string} url
   * @param {ErrorCode[]} [wellKnownErrorCodes] Well-known error codes that if returned should not be logged
   * @return {Promise}
   */
  public static async delete(url: string, wellKnownErrorCodes: ErrorCode[] = []): Promise<any> {
    try {
      const response = await axios.delete(url, this.createAxiosConfig());
      return response.data;
    } catch (error: any) {
      const applicationError = errorResponseHandler.handleErrorResponse(error, wellKnownErrorCodes);
      throw applicationError;
    }
  }

  /**
   * Returns a promise for a POST request with binary data. Post should be used to create a new resource with custom data type
   * @param {string} url
   * @param {*} data JavaScript object
   * @param {ErrorCode[]} [wellKnownErrorCodes] Well-known error codes that if returned should not be logged
   * @return {Promise}
   */
  public static async postBinaryData(url: string, data: any, wellKnownErrorCodes: ErrorCode[] = []): Promise<any> {
    try {
      const config: AxiosRequestConfig = this.createAxiosConfig(data);
      config.responseType = 'arraybuffer'; // Set response type to 'arraybuffer' for binary data

      const response = await axios.post(url, data, config);
      return response.data;
    } catch (error: any) {
      const applicationError = errorResponseHandler.handleErrorResponse(error, wellKnownErrorCodes);
      throw applicationError;
    }
  }

  /**
 * Executes a download post request for the given parameters and handles adding the access token, the accept language, Persona ID and Business ID.
 * @param {string} url
 * @param {object} [params]
 */
  public static async downloadPostRequest(url: string, params: Record<string, any> = {}): Promise<void> {
    const contextData = useContextDataStore();
    const token = tokenManager.getAccessToken();
    const form = document.createElement('form');
    form.setAttribute('action', `${url}?access_token=${token}`);
    form.setAttribute('method', 'post');
    form.setAttribute('target', '_blank');

    const defaultParams = {
      AcceptLanguage: contextData.languageCode,
      PersonaId: contextData.userData?.personaId,
      BusinessId: contextData.userData?.business?.businessId
    };

    const combinedParams = { ...defaultParams, ...params };

    Object.keys(combinedParams).forEach(key => {
      const input = document.createElement('input');
      input.setAttribute('type', 'hidden');
      input.setAttribute('name', key);
      input.setAttribute('value', combinedParams[key]);
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  }

  /**
   * Creates an Axios configuration object for the given parameters and handles adding the bearer token
   * HTTP header
   * @param {any} data Indicates if the request has data to be sent
   * @return {AxiosRequestConfig}
   */
  private static createAxiosConfig(data?: any): AxiosRequestConfig {
    const contextData = useContextDataStore();
    const config: AxiosRequestConfig = {
      headers: {
        'Accept-Language': contextData.languageCode,
        'Authorization': 'Bearer ' + tokenManager.getAccessToken()
      },
      timeout: contextData.inReleaseMode ? 45000 : undefined
    };

    if (data) {
      config.headers['Content-Type'] = 'application/json';
    }

    if (contextData.userData) {
      const personaId = contextData.userData.personaId;
      if (personaId) {
        config.headers['PersonaId'] = contextData.userData?.personaId;
      }
      const businessId = contextData.userData?.business?.businessId;
      if (businessId) {
        config.headers['BusinessId'] = contextData.userData?.business?.businessId;
      }
    }

    return config;
  }
}